import React, { useState } from 'react';
import Box from 'components/elements/Box';
import Button from 'components/elements/button';
import Modal from 'components/utility/Modal/Modal';
import Table from 'components/data-visualization/Table/Table';
import Row from 'components/data-visualization/Table/Row';
import ColumnHeader from 'components/data-visualization/Table/Header';
import Cell from 'components/data-visualization/Table/Cell';
import ViewContainer from './ViewContainer';
import { IAvailableCustomerResult, IHistoryData } from '../shared/shared';
import formatDate from 'helpers/formatDate';
import { TableHeader, TableBody, JobStatus } from '../shared/styles';
import Span from 'components/elements/span';
import arrayToObject from 'helpers/arrayToObject';
import colors from 'styles/themes/colors';

interface IViewHistoryProps {
  historyList?: IHistoryData[];
  jobName: string;
  shouldGetStatus: (shouldGet: boolean) => void;
  customers: IAvailableCustomerResult[];
}
interface IHistoryProps extends IViewHistoryProps {
  hide: () => void;
}

const History: React.FC<IHistoryProps> = ({
  hide,
  jobName,
  historyList,
  customers,
}) => {
  const customersObj = arrayToObject(customers, 'id');
  return (
    <ViewContainer title={jobName} close={(): void => hide()}>
      <>
        <Box
          display="flex"
          justifyContent="space-around"
          flexWrap="wrap"
          p={2}
          maxHeight="490px"
          overflowX="scroll"
          borderBottom={`1px solid ${colors.LightGrey}`}
        >
          <Table>
            <TableHeader>
              <Row>
                <ColumnHeader>Run Date</ColumnHeader>
                <ColumnHeader>Export Date</ColumnHeader>
                <ColumnHeader>Status</ColumnHeader>
                <ColumnHeader>Started By</ColumnHeader>
                <ColumnHeader>Rows by Customers</ColumnHeader>
                <ColumnHeader>
                  Total Rows
                  <br />
                  Exported
                </ColumnHeader>
              </Row>
            </TableHeader>
            <TableBody>
              {historyList?.map((historyItem: IHistoryData) => (
                <Row key={`${historyItem.startDate}_${historyItem.endDate}`}>
                  <Cell>
                    {formatDate(new Date(historyItem.endDate), 'day-short')}
                  </Cell>
                  <Cell>
                    {formatDate(
                      new Date(historyItem.dateSelectionStart),
                      'day-short'
                    )}
                  </Cell>
                  <Cell>
                    <JobStatus status={historyItem.runStatus}>
                      {historyItem.runStatus}
                    </JobStatus>
                  </Cell>
                  <Cell>{historyItem.startedBy}</Cell>
                  <Cell>
                    {historyItem.details.map((detail) => (
                      <div key={detail.customerId}>
                        <Span
                          mr={1}
                          color={colors.LightNightBlue}
                          fontWeight="600"
                        >
                          {customersObj[detail.customerId].displayName}:
                        </Span>
                        {detail.rowsExported}
                      </div>
                    ))}
                  </Cell>
                  <Cell>{historyItem.totalRowsExported}</Cell>
                </Row>
              ))}
            </TableBody>
          </Table>
        </Box>
        <Box display="flex" justifyContent="center">
          <Button
            onClick={hide}
            variant="secondary"
            alignSelf="center"
            justifySelf="center"
            fontWeight="600"
            borderRadius="3px"
            height="28px"
            py={1}
            px={6}
            mt={3}
            mb={2}
            mx={2}
            type="button"
          >
            Close
          </Button>
        </Box>
      </>
    </ViewContainer>
  );
};

const ViewHistory: React.FC<IViewHistoryProps> = (props) => {
  const [showModal, setShowModal] = useState(false);

  const onButtonClick = (show: boolean): void => {
    setShowModal(show);
    props.shouldGetStatus(!show);
  };
  return (
    <>
      <Button
        variant="primary"
        display="flex"
        alignItems="center"
        onClick={(): void => onButtonClick(true)}
        type="button"
        px={3}
        py={1}
        fontSize="12px"
        borderRadius="3px"
      >
        Open History
      </Button>
      {showModal && (
        <Modal
          maxWidth="55em"
          isNarrow={false}
          hide={(): void => onButtonClick(false)}
          canClickOutside={true}
        >
          <History {...props} hide={(): void => onButtonClick(false)} />
        </Modal>
      )}
    </>
  );
};

export default ViewHistory;
